import React from 'react';
import { TrendingUp, DollarSign, Award, Home, Palmtree, Brain } from 'lucide-react';

const BusinessValueSection = () => {
  return (
    <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 rounded-2xl p-8 border-2 border-purple-500/30">
      
      <div className="text-center mb-8">
        <div className="inline-block bg-purple-600/20 border border-purple-500 rounded-full px-6 py-3 mb-4">
          <p className="text-purple-400 font-bold text-lg flex items-center justify-center">
            <Brain className="mr-2" size={24} />
            Next-Level Thinking
          </p>
        </div>
        <h3 className="text-3xl md:text-4xl font-black text-white mb-4">
          This Makes Your Business<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
            Worth 3x More When You Sell It
          </span>
        </h3>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
          One day, you'll want to retire. Or cash out. Or just slow down.<br />
          This system makes that possible (and profitable).
        </p>
      </div>

      <div className="max-w-4xl mx-auto">

        {/* The Reality */}
        <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 mb-6">
          <h4 className="text-2xl font-bold text-white mb-4 flex items-center">
            <TrendingUp className="text-yellow-400 mr-3" size={32} />
            Here's What Buyers Actually Pay For:
          </h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-red-900/20 rounded-lg p-4 border border-red-500/30">
              <p className="text-red-400 font-bold mb-2">❌ Low Value Business:</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• Everything's in your head</li>
                <li>• YOU answer all the calls</li>
                <li>• YOU do all the quotes</li>
                <li>• No systems, just chaos</li>
                <li>• Customer data scattered everywhere</li>
                <li>• New owner has to rebuild everything</li>
              </ul>
              <p className="text-red-400 font-bold mt-3">Worth: 1-2x annual profit</p>
            </div>
            <div className="bg-green-900/20 rounded-lg p-4 border border-green-500/30">
              <p className="text-green-400 font-bold mb-2">✅ High Value Business:</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• System handles calls automatically</li>
                <li>• Quotes go out without you</li>
                <li>• Follow-ups happen automatically</li>
                <li>• All customer data organised</li>
                <li>• Reviews coming in constantly</li>
                <li>• New owner gets turnkey operation</li>
              </ul>
              <p className="text-green-400 font-bold mt-3">Worth: 3-5x annual profit</p>
            </div>
          </div>
        </div>

        {/* The Scenario */}
        <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border-2 border-blue-500 rounded-xl p-6 mb-6">
          <h4 className="text-2xl font-bold text-white mb-4 text-center">
            Imagine This Scenario:
          </h4>
          
          <div className="space-y-4 text-gray-200">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="font-bold text-cyan-400 mb-2">You're 55. You've been at this for 25 years.</p>
              <p>Your back hurts. You want to spend winters in Spain. Maybe see the grandkids more.</p>
            </div>

            <div className="bg-white/5 rounded-lg p-4">
              <p className="font-bold text-cyan-400 mb-2">Your mate Dave sold his plumbing business last year.</p>
              <p>He was turning over £200K/year. Got offered £150K for it. Barely 1x profit. Why? Because without Dave, the business WAS nothing. No systems. No processes. Just Dave's mobile number and a van.</p>
            </div>

            <div className="bg-white/5 rounded-lg p-4">
              <p className="font-bold text-cyan-400 mb-2">But YOU have this system.</p>
              <p>Calls get answered automatically. Quotes go out without you. Follow-ups happen. Reviews come in. Customer database is organised. New owner just needs to show up and do the work.</p>
            </div>

            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-4">
              <p className="font-bold text-white mb-2 text-lg">You're turning over £200K/year too.</p>
              <p className="text-white">But you get offered <strong className="text-2xl text-yellow-300">£600K</strong>.</p>
              <p className="text-green-100 text-sm mt-2">That's the difference between a job and a business. Between £150K and £600K. Between working until you're 70 and retiring at 55.</p>
            </div>
          </div>
        </div>

        {/* What This Means */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 text-center">
            <div className="bg-blue-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <DollarSign className="text-blue-400" size={32} />
            </div>
            <h4 className="text-xl font-bold text-white mb-2">Higher Sale Price</h4>
            <p className="text-gray-300 text-sm">
              Buyers pay more for systems that run without you. 3-5x profit vs 1-2x.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 text-center">
            <div className="bg-purple-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Award className="text-purple-400" size={32} />
            </div>
            <h4 className="text-xl font-bold text-white mb-2">More Buyers</h4>
            <p className="text-gray-300 text-sm">
              Turnkey business = easier to sell. You're not just selling a job, you're selling an asset.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 text-center">
            <div className="bg-green-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Home className="text-green-400" size={32} />
            </div>
            <h4 className="text-xl font-bold text-white mb-2">Your Choice</h4>
            <p className="text-gray-300 text-sm">
              Retire early. Cash out. Pass it to your kids. Whatever YOU want. Not trapped forever.
            </p>
          </div>
        </div>

        {/* The Dream */}
        <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border-2 border-yellow-500 rounded-xl p-6">
          <div className="text-center">
            <Palmtree className="text-yellow-400 mx-auto mb-4" size={48} />
            <h4 className="text-2xl font-bold text-white mb-4">
              The £497/Month That Paid For Your Retirement
            </h4>
            <p className="text-gray-200 text-lg mb-4">
              You're not just paying £497/month for a system.<br />
              You're building an asset worth <strong className="text-yellow-400">hundreds of thousands more</strong> when you sell.
            </p>
            <p className="text-gray-300">
              Dave got £150K and has to keep working somewhere to pay the bills.<br />
              You get £600K, buy a villa in Spain, and never have to work again.<br />
              <strong className="text-white text-xl">That's the difference.</strong>
            </p>
          </div>
        </div>

        {/* Reality Check */}
        <div className="mt-6 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <p className="text-gray-300 text-center">
            <strong className="text-white">Look, you might never sell.</strong> Maybe you pass it to your kids. Maybe you run it forever. That's fine.<br />
            But wouldn't it be nice to have the <span className="text-purple-400 font-bold">OPTION</span>?<br />
            This system gives you options. Dave had none.
          </p>
        </div>

      </div>

    </div>
  );
};

export default BusinessValueSection;
